import React, { JSX } from 'react';
import {
    Edit,
    DateInput,
    SelectInput,
    ReferenceField,
    TextInput,
    ImageInput,
    ImageField,
    required,
    BooleanInput,
    TabbedForm,
    DateField,
    FormDataConsumer,
    FormDataConsumerRenderParams,
    WithRecord,
    Link,
    SimpleFormIterator,
    ArrayInput,
    Datagrid,
    TextField,
    ShowButton,
    EditButton,
    ReferenceManyField,
    SelectField,
    NumberInput
} from 'react-admin';
import { accountTypes, accountStatus, invoiceTypes, currency } from "../../defaults/defaults";
import { Box, Grid } from "@mui/material";
import { CustomerAccount } from "../../types/entities";
import { LinearProgress } from "@mui/material";
import { useGetIndustries, useStripeModeFetch } from "../global-hooks/default-system-hooks";
import { SubscriptionManagerContainer } from "./components/CustomerAccount/SubscriptionManagerContainer";
import { useGetCountries } from "../global-hooks/default-system-hooks";
import { CustomerAccountNewCompanyModal } from "./components/CustomerAccount/CustomerAccountNewCompanyModal";
import { CompanyDomainsToggle } from "./components/CustomerAccount/CompanyDomainsToggle";
import { SingleChargeContainer } from "./components/CustomerAccount/SingleChargeContainer";
import { CustomerAccountCreditManager } from "./components/CustomerAccount/CustomerAccountCreditManager";
import { CustomerAccountMiniContainer } from "./components/CustomerAccount/CustomerAccountMiniContainer";
import { CustomerAccountActivityPanel } from "./components/CustomerAccount/CustomerAccountActivityPanel";
import { ChatFieldBridge } from "../chat-bot/components/ChatFieldBridge";
import {
    AllowanceContextValue,
    CustomerAccountAllowanceProvider,
    useCustomerAccountAllowances
} from "./components/CustomerAccount/CustomerAccountAllowanceProvider";
import { useAccountAllowances } from "./components/CustomerAccount/hooks/customer-account-allowances";
import { useParams } from "react-router-dom";
import { CustomerAccountAllowanceBar } from "./components/CustomerAccount/CustomerAccountAllowanceBar";
import { CustomerAccountSeatsPanel } from "./components/CustomerAccount/CustomerAccountSeatsPanel";
import { CustomerAccountCompanyDomains } from "./components/CustomerAccount/CustomerAccountCompanyDomains";

/**
 * What the account's plan allows, on a tab that can spend it. The API refuses an
 * over-allowance write on its own account; this is so the admin is told before they try.
 */
const AllowanceBand: (props: { pools: ('companies'|'domains'|'seats')[] }) => JSX.Element =
    (props: { pools: ('companies'|'domains'|'seats')[] }): JSX.Element => {
        const { loadingAllowances, allowancesError, allowances } = useCustomerAccountAllowances();

        return (
            <CustomerAccountAllowanceBar allowances={ allowances }
                                         loading={ loadingAllowances }
                                         error={ allowancesError }
                                         pools={ props.pools } />
        );
    }

export const CustomerAccountEdit: () => JSX.Element = (): JSX.Element => {
    const { isTestMode, loadingTestModeData } = useStripeModeFetch();
    const { loading, loadedCountries } = useGetCountries();
    const { loadedIndustries, loadingIndustries } = useGetIndustries();
    // The id comes from the route rather than the record, because this component sits
    // outside `<Edit>` and there is no record context here yet.
    const { id } = useParams();
    const allowanceContext: AllowanceContextValue = useAccountAllowances(id);

    const inputText = choice => `${choice.name}`;

    return (
        <Edit redirect={ false }>
            <CustomerAccountAllowanceProvider value={ allowanceContext }>
            <TabbedForm>
                <TabbedForm.Tab label="General">
                    <Grid container spacing={ 2 } sx={ { width: '100%' } }>

                        <Grid size={ 6 } sx={ { display: 'flex', flexDirection: 'column' } }>
                            <SelectInput name="type" source="type" choices={ accountTypes } defaultValue="personal" validate={ [required()] } />
                            <SelectInput name="status" source="status" choices={ accountStatus } defaultValue="new" validate={ [required()] } />
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems:'center' }}>
                                <span>{ "Account Holder:" }</span>
                                <ReferenceField source="accountHolder" reference="customers" />
                            </Box>
                        </Grid>
                        <Grid size={ 6 } sx={ { display: 'flex', flexDirection: 'column' } }>
                            <CustomerAccountCreditManager />
                            <Box sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems:'center',
                                marginBottom: '1rem'
                            }}>
                                <Box sx={{ width: '45%', display: 'flex', justifyContent: 'space-between', alignItems:'center' }}>
                                    <span>{ "Last Updated:" }</span>
                                    <DateField source="updatedAt" showTime/>
                                </Box>
                                <Box sx={{ width: '45%', display: 'flex', justifyContent: 'space-between', alignItems:'center' }}>
                                    <span>{ "Created:" }</span>
                                    <DateField source="createdAt" showTime/>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    {/* Inside a tab, not beside them: TabbedFormTabs clones every direct child into the
                        tab bar. One tab is enough - react-admin mounts them all and hides the
                        inactive ones, so the whole form is registered from here. */}
                    <ChatFieldBridge />
                </TabbedForm.Tab>
                <TabbedForm.Tab label="Billing Address">
                    <ArrayInput source="billingAddress" name="billingAddress"  >
                        <SimpleFormIterator  >
                            <TextInput source="lineOne" validate={ [required()] } />
                            <TextInput source="lineTwo"  />
                            <TextInput source="city" validate={ [required()] } />
                            <TextInput source="county"  />
                            { (loading) &&
                                <LinearProgress sx={ { margin: '10px 0', width: '75%' } } />
                            }
                            { (!loading) &&
                                <SelectInput source="country" choices={ loadedCountries } />
                            }
                            <TextInput source="postcode" validate={ [required()] } />
                            <BooleanInput source="defaultAddress"
                                          helperText={ "The first default address value found will be used. Please keep only one as default" }
                                           />
                        </SimpleFormIterator>
                    </ArrayInput>
                </TabbedForm.Tab>
                { /* "Company and Domain" since 2026-09-10, at the user's instruction: adding a
                     domain is now done here, from the expanded half of a company's row, and the
                     tab's name says so. */ }
                <TabbedForm.Tab label="Company and Domain">
                    { /* Both of the modal's tabs spend the company pool: one creates a company
                         against the account, the other attaches companies that already exist.
                         Domains are shown beside it because a company is not usable without one. */ }
                    <AllowanceBand pools={ ['companies', 'domains'] } />
                    <WithRecord render={
                        (record: CustomerAccount): JSX.Element => {
                            return (
                                <CustomerAccountNewCompanyModal record={ record } />
                            );
                        }
                    } label=""/>
                    {/*<ReferenceArrayInput source="companies" reference="companies" >*/}
                    {/*    <AutocompleteArrayInput />*/}
                    {/*</ReferenceArrayInput>*/}
                    <ReferenceManyField
                        label="Companies"
                        source="companies"
                        reference="companies"
                        target="id"
                    >
                        { /* Expand a company to see its domains and add one. The company screen
                             carries the same panel. */ }
                        <Datagrid bulkActionButtons={ false }
                                  sx={ { width: '100%' } }
                                  expand={ <CustomerAccountCompanyDomains /> } >
                            <TextField source="id" sx={ { textTransform: 'capitalize' } } />
                            <TextField source="name"  sx={ { textTransform: 'uppercase' } } />
                            <WithRecord label="Domains" render={ (): JSX.Element => <CompanyDomainsToggle /> } />
                            <TextField source="sicCode"/>
                            <TextField source="yearEstablished" label={ "Year Established" } />
                            { (loadingIndustries) &&
                                <LinearProgress sx={ { margin: '10px 0', width: '50%' } } />
                            }
                            { (!loadingIndustries) &&
                                <SelectField
                                    source="industry"
                                    choices={ loadedIndustries }
                                    optionValue="id"
                                />
                            }
                            { /* No ShowButton: the company Show screen was merged into its Edit on
                                 2026-09-10 and the route is gone. */ }
                            <EditButton />

                        </Datagrid>
                    </ReferenceManyField>

                </TabbedForm.Tab>
                <TabbedForm.Tab label="Seats">
                    <CustomerAccountSeatsPanel />
                </TabbedForm.Tab>
                <TabbedForm.Tab label="Subscription">
                    <Grid container spacing={ 2 } sx={ { width: '100%' } }>
                        <Grid size={ 6 } sx={ { display: 'flex', flexDirection: 'column' } }>
                            <DateInput source="trialEndDate" name="trialEndDate" />
                        </Grid>
                        <Grid size={ 6 } sx={ { display: 'flex', flexDirection: 'column' } }>
                            <BooleanInput source="trialCompleted" name="trialCompleted" />
                        </Grid>
                    </Grid>
                    <SubscriptionManagerContainer />
                </TabbedForm.Tab>
                <TabbedForm.Tab label="Single Charge">
                    <SingleChargeContainer />
                </TabbedForm.Tab>
                <TabbedForm.Tab label="Invoices">
                    <ReferenceManyField
                        label="Invoices"
                        source="subscriptionInvoices"
                        reference="subscription_invoices"
                        target="id"
                    >
                        <Datagrid bulkActionButtons={ false } sx={ { width: '100%' } } >
                            <TextField source="id" sx={ { textTransform: 'capitalize' } } />
                            <TextField source="status"  sx={ { textTransform: 'uppercase' } } />
                            <SelectField source="currency" choices={ currency } />
                            <TextField source="amountPaid"/>
                            <TextField source="invoiceId" label={ "Invoice ID" } />
                            <SelectField source="invoiceType" choices={ invoiceTypes } />
                            <ShowButton label={ "View" }/>
                        </Datagrid>
                    </ReferenceManyField>
                </TabbedForm.Tab>
                <TabbedForm.Tab label="Stripe Data">
                    <WithRecord render={
                        (record: CustomerAccount): JSX.Element => {
                            if (!record.stripeId) {
                                return (
                                    <Box sx={ { width: '100%', margin: '10px 0', display: 'flex', justifyContent: 'space-between' } } >
                                        <span>{ "Stripe Customer ID:" }</span>
                                        <span>{ "Not yet available" }</span>
                                    </Box>
                                );
                            }

                            return (
                                <Box sx={ { width: '100%', margin: '10px 0', display: 'flex', justifyContent: 'space-between' } } >
                                    <span>{ "Stripe Customer ID:" }</span>
                                    { (loadingTestModeData) &&
                                        <LinearProgress sx={ { margin: '10px 0', width: '75%' } } />
                                    }
                                    { (!loadingTestModeData) &&
                                        <span>
                                            { (isTestMode) &&
                                                <Link to={ "https://dashboard.stripe.com/acct_1S676LIYyPSARt7l/test/customers/" + record.stripeId }
                                                      target="_blank"
                                                >{ record.stripeId }</Link>
                                            }
                                            {(!isTestMode) &&
                                                <Link to={ "https://dashboard.stripe.com/acct_1S676LIYyPSARt7l/customers/" + record.stripeId }
                                                      target="_blank"
                                                >{ record.stripeId }</Link>
                                            }
                                        </span>
                                    }
                                </Box>
                            );
                        }
                    } />
                </TabbedForm.Tab>
                <TabbedForm.Tab label="History">
                    <CustomerAccountActivityPanel />
                </TabbedForm.Tab>
                <TabbedForm.Tab label="Minis">
                    <CustomerAccountMiniContainer />
                </TabbedForm.Tab>
            </TabbedForm>
            </CustomerAccountAllowanceProvider>
        </Edit>
    );
}
